import { Box, Container, Typography, Avatar, Card, CardContent, Grid, Stack, Divider } from '@mui/material';
import { ArrowBackOutlined as ArrowBackOutlinedIcon } from '@mui/icons-material';
import { fetchMyCollaborations } from '../../lib/api';
import { formatDate, formatDateRange } from '../../lib/format';
import { navigate } from '../../lib/navigation';
import { useOwnedSection } from '../../lib/useOwnedSection';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import SectionLoading from '../../components/SectionLoading';

function CollaborationDetailPage({ id }) {
  const { items: collaborations, loading } = useOwnedSection(fetchMyCollaborations, '/v1/profiles/me/collaborations', (next) => ({
    collaborations: next,
  }));

  if (loading) return <SectionLoading />;

  const collab = collaborations.find((item) => item.id === id);
  if (!collab) return <EmptyState title="Collaboration not found" text="Return to the collaboration list to choose another record." />;
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader
        title={collab.campaign}
        description={`${collab.brand} campaign summary for your creator profile and media kit.`}
        action={
          <Button variant="secondary" onClick={() => navigate('/dashboard/collaborations')} startIcon={<ArrowBackOutlinedIcon />}>
            Back to brand history
          </Button>
        }
      />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                <Avatar src={collab.brandLogoUrl} variant="rounded" sx={{ width: 56, height: 56 }} />
                <Box>
                  <Typography variant="h6">{collab.brand}</Typography>
                  <Typography color="text.secondary">{collab.description}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Badge>{collab.type}</Badge>
                <Badge>{collab.status}</Badge>
                <Badge tone={collab.visibility === 'Public' ? 'success' : 'neutral'}>{collab.visibility}</Badge>
              </Stack>
              {formatDateRange(collab.startDate, collab.endDate) && (
                <Typography variant="body2" color="text.secondary">
                  {formatDateRange(collab.startDate, collab.endDate)}
                </Typography>
              )}
              {collab.testimonial && (
                <Typography
                  component="blockquote"
                  variant="body2"
                  sx={{ mt: 2, pl: 2, borderLeft: 3, borderColor: 'primary.main', fontStyle: 'italic' }}
                >
                  “{collab.testimonial}”
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content links
              </Typography>
              <Stack divider={<Divider />} spacing={1.5}>
                {collab.deliverables.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No content links added.
                  </Typography>
                ) : (
                  collab.deliverables.map((item) => (
                    <Stack direction="row" key={item.id} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography fontWeight={600}>{item.platform}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.status}
                          {item.dueDate ? ` · Due ${formatDate(item.dueDate)}` : ''}
                        </Typography>
                      </Box>
                      {item.contentUrl ? (
                        <Button variant="secondary" href={item.contentUrl} target="_blank" rel="noreferrer">
                          Open
                        </Button>
                      ) : (
                        <Badge>Not added</Badge>
                      )}
                    </Stack>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default CollaborationDetailPage;
